import React, { useRef, useEffect } from 'react';

/**
 * Animated dot-matrix reveal effect using HTML5 Canvas.
 * Zero npm dependencies — works with any React version.
 */
const CanvasRevealEffect = ({
  dotSize = 4,
  gridSize = 20,
  colors = [[255, 255, 255]],
  opacities = [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1],
  animationSpeed = 3,
  showGradient = true,
  reverse = false,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let rafId = null;
    let startTime = null;
    let dots = [];

    const buildDots = () => {
      const W = canvas.width;
      const H = canvas.height;
      const cols = Math.ceil(W / gridSize) + 1;
      const rows = Math.ceil(H / gridSize) + 1;
      const cx = cols / 2;
      const cy = rows / 2;
      const maxDist = Math.sqrt(cx * cx + cy * cy);

      dots = [];
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          const dist = Math.sqrt((c - cx) ** 2 + (r - cy) ** 2);
          const jitter = Math.random() * 0.15;
          const timing = reverse
            ? (maxDist - dist) * 0.02 + jitter
            : dist * 0.01 + jitter;
          const color = colors[Math.floor(Math.random() * colors.length)];
          const maxAlpha = opacities[Math.floor(Math.random() * opacities.length)];
          // random flicker interval 3–7 s
          const flickerPeriod = 3 + Math.random() * 4;
          const flickerOffset = Math.random() * flickerPeriod;
          dots.push({ x: c * gridSize, y: r * gridSize, timing, maxAlpha, color, flickerPeriod, flickerOffset });
        }
      }
    };

    const resize = () => {
      canvas.width  = canvas.offsetWidth  * (window.devicePixelRatio || 1);
      canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
      buildDots();
    };

    resize();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(resize)
      : null;
    if (ro) ro.observe(canvas);

    const speedFactor = animationSpeed * 0.12;

    const draw = (ts) => {
      if (!startTime) startTime = ts;
      const t = ((ts - startTime) / 1000) * speedFactor;
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;

      ctx.clearRect(0, 0, W, H);

      for (const d of dots) {
        const progress = t - d.timing;
        if (progress <= 0) continue;

        // reveal fade-in (first 0.1 s of each dot's life)
        const reveal = Math.min(1, progress / 0.1);

        // gentle flicker after reveal
        const flicker = 0.5 + 0.5 * Math.sin(
          ((t + d.flickerOffset) / d.flickerPeriod) * Math.PI * 2
        );

        const alpha = reveal * d.maxAlpha * flicker;
        if (alpha < 0.01) continue;

        const [r, g, b] = d.color;
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, dotSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ro) ro.unobserve(canvas);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      {showGradient && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
};

export default CanvasRevealEffect;
